import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardBody, DataTable, Icon, CodeDisplay } from '@aidkitorg/component-library';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { useSpinDelay } from 'spin-delay';
import { usePost } from '../../../API';
import { useLocalizedStrings } from '../../../Localization';
import { IconName } from '@aidkitorg/component-library/src/Icon';
import { ApplicantData, EmptyStateCardProps, ApplicantDataTableProps } from './types';

const EmptyStateCard: React.FC<EmptyStateCardProps> = ({
  iconName,
  title,
  message,
}) => {
  return (
    <Card className="rounded-md w-100">
      <CardBody>
        <div className="flex items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <Icon name={iconName as IconName} type="solid" size="lg" className="mx-auto mb-4 text-gray-400" />
            <div className="text-lg font-medium mb-2">{title}</div>
            {message && <div className="text-sm text-gray-600">{message}</div>}
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export const ApplicantDataTable: React.FC<ApplicantDataTableProps> = ({
  filters,
  applicantGroupId,
  isLoading: externalLoading = false,
  error: externalError = false,
}) => {
  const L = useLocalizedStrings();
  const strings = L.admin_panel.settings.applicant_groups;
  const [applicantData, setApplicantData] = useState<ApplicantData | null>(null);
  const [isInternalLoading, setIsInternalLoading] = useState(false);
  const [internalError, setInternalError] = useState<boolean>(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [sorting, setSorting] = useState<SortingState>([]);

  const generateApplicantData = usePost('/api/admin_panel/applicant_groups/generate');

  // Combined loading and error states
  const isLoading = externalLoading || isInternalLoading;
  const error = externalError || internalError;

  // Use spin-delay to prevent flashing loading spinner
  const showLoadingSpinner = useSpinDelay(isLoading, {
    delay: 250,
    minDuration: 250
  });

  // Fetch applicant data based on filters or applicantGroupId
  const fetchApplicantData = useCallback(async () => {
    // If using applicantGroupId mode, always fetch
    // If using filters mode, only fetch if filters are enabled and properly configured
    if (!applicantGroupId && (!filters || !filters.enabled || !filters.config ||
        !filters.config.logic || !filters.config.conditions ||
        filters.config.conditions.length === 0)) {
      setApplicantData(null);
      return;
    }

    setIsInternalLoading(true);
    setInternalError(false);

    try {
      // Convert TanStack Table sorting format to backend format
      const backendSorting = sorting.map((s) => ({
        sortBy: s.id,
        sortOrder: s.desc ? ("desc" as const) : ("asc" as const),
      }));

      // Call our applicant groups generate endpoint
      const response = await generateApplicantData({
        ...(applicantGroupId ? { applicantGroupId } : { filters }),
        page,
        limit,
        sorting: backendSorting
      });

      if (response?.success && response.data) {
        setApplicantData(response.data);
      } else {
        throw new Error(strings.error_message);
      }
    } catch (err) {
      console.error('Error fetching applicant data:', err);
      setInternalError(true);
      setApplicantData(null);
    } finally {
      setIsInternalLoading(false);
    }
  }, [filters, applicantGroupId, page, limit, sorting, generateApplicantData]);

  // Fetch data when filters or pagination changes
  useEffect(() => {
    fetchApplicantData();
  }, [fetchApplicantData]);

  // Reset to page 1 when filters or applicantGroupId change
  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [filters, applicantGroupId]);

  // Generate columns from applicant data
  const columns = useMemo((): ColumnDef<any>[] => {
    if (!applicantData || applicantData.columns.length === 0) {
      return [];
    }

    return applicantData.columns.map(col => ({
      accessorKey: col.key,
      header: col.label,
      enableSorting: col.key !== 'uid',
      sortingFn: 'auto',
      sortDescFirst: false,
      cell: ({ getValue }: any) => {
        const value = getValue();

        if (value === null || value === undefined) {
          return <span className="inline-block min-h-[14px]">--</span>;
        }

        // Use CodeDisplay for UID columns
        if (col.key === 'uid') {
          return <CodeDisplay displayMode="inline" code={String(value)} copyable={true} className="text-xs" />;
        }

        return <span className="inline-block min-h-[14px]">
          {String(value)}
        </span>;
      }
    }));
  }, [applicantData]);

  // Handle pagination changes
  const onPaginationChange = useCallback((pagination: { pageIndex: number; pageSize: number }) => {
    const newPage = pagination.pageIndex + 1; // Convert 0-based to 1-based
    const newLimit = pagination.pageSize;

    // If page size changed, reset to page 1
    if (newLimit !== limit) {
      setPage(1);
      setLimit(newLimit);
    } else if (newPage !== page) {
      setPage(newPage);
    }
  }, [page, limit]);

  // Handle sorting changes
  const onSortingChange = useCallback((updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
    const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    setSorting(newSorting);
  }, [sorting]);

  // Show configuration prompt when no filters are set (only in filter mode)
  if (!applicantGroupId && (!filters || !filters.enabled || !filters.config ||
      !filters.config.logic || !filters.config.conditions ||
      filters.config.conditions.length === 0)) {
    return (
      <EmptyStateCard
        iconName="FunnelIcon"
        title={strings.matching_applicants}
        message={strings.configure_filters}
      />
    );
  }

  // Show error state
  if (error) {
    return (
      <EmptyStateCard
        iconName="ExclamationTriangleIcon"
        title={strings.error_message}
      />
    );
  }

  // Show empty state when filters result in no data
  if (applicantData && applicantData.data && applicantData.data.length === 0) {
    return (
      <EmptyStateCard
        iconName="MagnifyingGlassIcon"
        title={strings.no_matching_applicants}
        message={strings.empty_state_message}
      />
    );
  }

  // Only show data table if we have applicant data
  if (!applicantData || !applicantData.data) {
    return null;
  }

  // Show data table
  return (
    <Card className="rounded-md w-100">
      <CardBody>
        <DataTable
          columns={columns}
          data={applicantData.data}
          showPagination={true}
          pageSize={limit}
          getRowId={(row: any, index: number) => row.uid || `row-${index}`}
          manualPagination={{
            pageCount: applicantData.total > 0 ? Math.ceil(applicantData.total / limit) : 0,
            pageIndex: Math.max(0, page - 1), // Convert 1-based to 0-based
            totalRows: applicantData.total || 0,
            onPaginationChange: onPaginationChange
          }}
          manualSorting={{
            sorting: sorting,
            onSortingChange: onSortingChange
          }}
          initializing={isLoading}
        />
      </CardBody>
    </Card>
  );
};